import { isObject } from "lodash";
import { SnakeCasedPropertiesDeep } from "type-fest";

// Simple snake_case implementation to compliment camlize.ts
function snakeCase(str: string): string {
  return str.replace(/([A-Z])/g, "_$1").toLowerCase();
}

type Snakeified<T> = SnakeCasedPropertiesDeep<T>;
const ignoreKeys = ["upgradePermissions"];

/**
 * Converts an object's keys from camelCase to snake_case recursively.
 * This is the reverse operation of the camelize function.
 *
 * @param obj - The object to convert
 * @returns A new object with snake_case keys
 *
 * @example
 * ```ts
 * const camelCased = { userId: 123, userProfile: { firstName: 'John' } };
 * const snakeCased = snakeify(camelCased);
 * // Result: { user_id: 123, user_profile: { first_name: 'John' } }
 * ```
 */
function snakeify<T extends object>(obj: T): Snakeified<T> {
  if (obj === null || obj === undefined) return obj as Snakeified<T>;

  return Object.keys(obj).reduce((acc, key) => {
    // Check if we should ignore this key before converting it
    if (ignoreKeys.includes(key)) {
      acc[key] = obj[key as keyof T];
      return acc;
    }

    const _key = typeof key === "string" ? snakeCase(key) : key;
    const value = obj[key as keyof T];
    if (Array.isArray(value)) {
      acc[_key] = value.map(item => (isObject(item) ? snakeify(item) : item));
    } else if (isObject(value) && !ignoreKeys.includes(_key)) {
      acc[_key] = snakeify(value);
    } else {
      acc[_key] = value;
    }
    return acc;
  }, {} as Snakeified<T>);
}
export default snakeify;
export type { Snakeified };
